import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Headphones, Plus, Clock, CheckCircle, AlertTriangle, Send, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'hr', label: 'HR Query' },
  { value: 'it', label: 'IT Support' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'access', label: 'Access Request' },
  { value: 'facilities', label: 'Facilities' },
  { value: 'general', label: 'General' },
];

const HelpdeskPage = () => {
  const { user, isHR } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/helpdesk/tickets`, { withCredentials: true });
      setTickets(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.subject.trim()) { toast.error('Subject required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/helpdesk/tickets`, form, { withCredentials: true });
      toast.success('Ticket created!');
      setCreateOpen(false);
      setForm({ subject: '', description: '', category: 'general', priority: 'medium' });
      fetchTickets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleUpdate = async () => {
    if (!selectedTicket) return;
    const body = {};
    if (newComment.trim()) body.comment = newComment.trim();
    if (newStatus && newStatus !== selectedTicket.status) body.status = newStatus;
    if (!body.comment && !body.status) { toast.error('Add a comment or change status'); return; }
    try {
      await axios.put(`${API_URL}/api/helpdesk/tickets/${selectedTicket.id}`, body, { withCredentials: true });
      toast.success('Ticket updated');
      setNewComment('');
      const updated = await axios.get(`${API_URL}/api/helpdesk/tickets`, { withCredentials: true });
      setTickets(updated.data);
      const fresh = updated.data.find(t => t.id === selectedTicket.id);
      if (fresh) setSelectedTicket(fresh);
    } catch (err) { toast.error('Failed to update'); }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      open: { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
      in_progress: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: AlertTriangle },
      resolved: { cls: 'badge-success', icon: CheckCircle },
      closed: { cls: 'bg-muted text-muted-foreground', icon: CheckCircle },
    };
    const c = cfg[status] || cfg.open;
    return <Badge className={`${c.cls} gap-1`}><c.icon className="w-3 h-3" /> {(status || 'open').replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (p) => {
    const colors = { critical: 'bg-red-600 text-white', high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-muted text-muted-foreground' };
    return <Badge className={colors[p] || colors.medium}>{p}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const stats = {
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length,
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="helpdesk-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Headphones className="w-7 h-7 text-indigo-500" /> Helpdesk</h1>
          <p className="text-muted-foreground">Submit and track HR/IT support requests</p>
        </div>
        <Button className="gap-2" onClick={() => setCreateOpen(true)} data-testid="create-ticket-btn"><Plus className="w-4 h-4" /> New Ticket</Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{stats.open}</p><p className="text-xs text-muted-foreground">Open</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{stats.in_progress}</p><p className="text-xs text-muted-foreground">In Progress</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600">{stats.resolved}</p><p className="text-xs text-muted-foreground">Resolved</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Tickets</CardTitle><CardDescription>{tickets.length} total</CardDescription></CardHeader>
        <CardContent>
          {tickets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Ticket #</th>{isHR && <th>Employee</th>}<th>Subject</th><th>Category</th><th>Priority</th><th>Status</th><th>SLA</th><th>Created</th></tr></thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedTicket(t); setNewStatus(t.status); setNewComment(''); }} data-testid={`ticket-${t.ticket_number}`}>
                      <td className="font-mono text-xs font-medium text-primary">{t.ticket_number}</td>
                      {isHR && <td className="text-sm">{t.employee_name}</td>}
                      <td className="font-medium text-sm max-w-xs truncate">{t.subject}</td>
                      <td><Badge variant="outline" className="text-xs">{CATEGORIES.find(c => c.value === t.category)?.label || t.category}</Badge></td>
                      <td>{getPriorityBadge(t.priority)}</td>
                      <td>{getStatusBadge(t.status)}</td>
                      <td className="text-xs font-mono">{t.sla_hours}h</td>
                      <td className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-center text-muted-foreground py-8">No tickets. Create one if you need help!</p>}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New Support Ticket</DialogTitle><DialogDescription>Describe your issue and we'll get back to you</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Subject</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Brief description" data-testid="ticket-subject" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Detailed description" data-testid="ticket-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button className="w-full gap-2" onClick={handleCreate} disabled={submitting} data-testid="submit-ticket-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Ticket
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Headphones className="w-5 h-5" /> {selectedTicket?.ticket_number}</DialogTitle></DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">{selectedTicket.subject}</h3>
                <p className="text-sm text-muted-foreground mt-1">{selectedTicket.description}</p>
                <div className="flex gap-2 mt-2">{getStatusBadge(selectedTicket.status)} {getPriorityBadge(selectedTicket.priority)}</div>
              </div>
              {selectedTicket.comments?.length > 0 && (
                <div className="space-y-2 border-t pt-3">
                  <p className="text-xs font-semibold text-muted-foreground">Comments</p>
                  {selectedTicket.comments.map(c => (
                    <div key={c.id} className="p-2 rounded bg-muted/30 text-sm">
                      <span className="font-medium">{c.author_name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{new Date(c.created_at).toLocaleString('en-IN')}</span>
                      <p className="mt-1">{c.text}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2 border-t pt-3">
                <Textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment..." rows={2} data-testid="ticket-comment" />
                {isHR && (
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Button size="sm" className="gap-1" onClick={handleUpdate} data-testid="update-ticket-btn"><MessageCircle className="w-3.5 h-3.5" /> Update</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpdeskPage;
